import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { SlinguAvatar } from './SlinguAvatar.js';
import { CheckIcon, LockIcon, PlayIcon, StarIcon } from './icons/index.js';
function statusIcon(status) {
    switch (status) {
        case 'completed':
            return _jsx(CheckIcon, { size: 22 });
        case 'in_progress':
            return _jsx(StarIcon, { size: 22 });
        case 'locked':
            return _jsx(LockIcon, { size: 20 });
        case 'available':
        default:
            return _jsx(PlayIcon, { size: 22 });
    }
}
const DECOR = [
    { type: 'cloud', x: 12 },
    { type: 'cloud', x: 78 },
    { type: 'star', x: 22 },
    { type: 'cloud', x: 86 },
    { type: 'star', x: 68 },
];
/**
 * Plataformas de pulo (estilo Flappy Bird / Doodle Jump) com Slingu posicionado
 * abaixo da próxima lição — ele "pula" pra plataforma quando você toca nela.
 * Antes de qualquer lição ser iniciada, Slingu fica numa plataforma "fase 0"
 * abaixo da fase 1.
 */
export function PlatformPath({ nodes, onStart }) {
    const reversed = useMemo(() => [...nodes].reverse(), [nodes]);
    // Se nenhuma lição foi concluída ainda, Slingu fica na fase 0 (plataforma
    // "start" abaixo da primeira). Assim que completar a primeira, ele deixa
    // de aparecer lá e passa a sentar na próxima disponível.
    const anyCompleted = nodes.some((n) => n.status === 'completed');
    const atStart = !anyCompleted;
    const activeIdx = nodes.findIndex((n) => n.status === 'available' || n.status === 'in_progress');
    const wrapRef = useRef(null);
    const [jumping, setJumping] = useState(false);
    useEffect(() => {
        if (!wrapRef.current)
            return;
        // Before first lesson: scroll to the bottom (start platform visible).
        if (atStart) {
            wrapRef.current.scrollTop = wrapRef.current.scrollHeight;
            return;
        }
        if (activeIdx < 0)
            return;
        const el = wrapRef.current.querySelector(`[data-platform-idx="${activeIdx}"]`);
        if (!el)
            return;
        const parent = wrapRef.current;
        const rect = el.getBoundingClientRect();
        const parentRect = parent.getBoundingClientRect();
        parent.scrollTop =
            rect.top - parentRect.top + parent.scrollTop - parent.clientHeight * 0.55;
    }, [activeIdx, atStart]);
    function handleTap(node) {
        if (node.status === 'locked')
            return;
        setJumping(true);
        setTimeout(() => {
            setJumping(false);
            onStart(node.id);
        }, 420);
    }
    return (_jsxs("div", { ref: wrapRef, className: "sl-platform-path", children: [_jsx("div", { className: "sl-platform-path__bg", "aria-hidden": true, children: DECOR.map((d, i) => (_jsx("span", { className: `sl-platform-path__decor sl-platform-path__decor--${d.type}`, style: { left: `${d.x}%`, top: `${(i * 23) % 100}%` } }, i))) }), _jsxs("ol", { className: "sl-platform-path__list", children: [reversed.map((node, revIdx) => {
                        const i = nodes.length - 1 - revIdx;
                        // Avatar só aparece na plataforma ativa quando já houve progresso.
                        const isActive = !atStart && i === activeIdx;
                        const isLeft = i % 2 === 0;
                        return (_jsxs("li", { "data-platform-idx": i, className: `sl-platform sl-platform--${node.status} ${isLeft ? 'sl-platform--left' : 'sl-platform--right'}${isActive ? ' sl-platform--active' : ''}`, children: [_jsxs("button", { type: "button", className: "sl-platform__tile", disabled: node.status === 'locked', onClick: () => handleTap(node), "aria-label": `${node.title} — ${node.status}`, children: [_jsx("span", { className: "sl-platform__icon", "aria-hidden": true, children: statusIcon(node.status) }), _jsx("span", { className: "sl-platform__title", children: node.title }), node.theme ? (_jsx("span", { className: "sl-platform__theme", children: node.theme })) : null, _jsxs("span", { className: "sl-platform__xp", children: ["+", node.xpReward, " XP"] })] }), isActive ? (_jsx("div", { className: `sl-avatar-pos${jumping ? ' sl-avatar-pos--jump' : ''}`, "aria-hidden": true, children: _jsx("div", { className: "sl-avatar-bob", children: _jsx(SlinguAvatar, { size: 74, mood: jumping ? 'happy' : 'idle' }) }) })) : null] }, node.id));
                    }), _jsxs("li", { className: `sl-platform sl-platform--start sl-platform--center${atStart ? ' sl-platform--active' : ''}`, "data-platform-idx": "-1", children: [_jsxs("div", { className: "sl-platform__tile sl-platform__tile--start", "aria-hidden": true, children: [_jsx("span", { className: "sl-platform__icon sl-platform__icon--start", children: _jsx(PlayIcon, { size: 20 }) }), _jsx("span", { className: "sl-platform__title", children: "Come\u00E7o" }), _jsx("span", { className: "sl-platform__xp", children: "Fase 0" })] }), atStart ? (_jsx("div", { className: `sl-avatar-pos sl-avatar-pos--start${jumping ? ' sl-avatar-pos--jump' : ''}`, "aria-hidden": true, children: _jsx("div", { className: "sl-avatar-bob", children: _jsx(SlinguAvatar, { size: 74, mood: jumping ? 'happy' : 'wave' }) }) })) : null] })] }), _jsx("div", { className: "sl-platform-path__ground", "aria-hidden": true })] }));
}
//# sourceMappingURL=PlatformPath.js.map